import React, { useState, useEffect } from 'react';
import StreamingSummaryTable from '../components/streaming/StreamingSummaryTableV2';
import StreamingTable from '../components/streaming/StreamingTableV2';
import { XMarkIcon, ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import MetricsChart from '../components/shared/MetricsChart';
import AddAlbumModal from '../components/shared/AddAlbumModal';
import RosterManager from '../components/roster/RosterManager';
import AddRosterSongModal from '../components/roster/AddRosterSongModal';
import { useAuth } from '../context/AuthContext';

const TABS = [
  { id: 'songs', label: 'Songs', type: 'song' },
  { id: 'albums', label: 'Albums', type: 'album' },
  { id: 'artists', label: 'Artists', type: 'artist' },
  { id: 'priority', label: 'Priority Songs', type: 'priority' }
];

const API_BASE_URL = process.env.REACT_APP_API_URL + '/streaming';
const ROSTER_MODE_KEY = 'encore.rosterManagementOn';

const readRosterMode = () => {
  try { return localStorage.getItem(ROSTER_MODE_KEY) === 'true'; } catch { return false; }
};

export default function StreamingPageV2() {
  const [activeTab, setActiveTab] = useState('songs');
  const [totals, setTotals] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [audioOnly, setAudioOnly] = useState(false);
  // Roster management view: only for roster_management users, off by default.
  const { hasPerm } = useAuth();
  const canManageRoster = hasPerm('roster_management');
  const [rosterModeOn, setRosterModeOn] = useState(readRosterMode);
  const [showAddRosterSong, setShowAddRosterSong] = useState(false);
  const rosterMode = canManageRoster && rosterModeOn;
  const toggleRosterMode = () => {
    const next = !rosterModeOn;
    setRosterModeOn(next);
    try { localStorage.setItem(ROSTER_MODE_KEY, String(next)); } catch { /* storage unavailable */ }
  };
  const [maxDates, setMaxDates] = useState({ weekly_date: null, daily_date: null });
  // Mobile breakpoint — used to compact the streaming-summary numbers.
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 640 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // Full comma format on desktop; compact (1.2M) on mobile to stay tight.
  const fmtTotal = (n) => {
    const v = Number(n) || 0;
    if (!isMobile) return v.toLocaleString();
    const a = Math.abs(v);
    const c = (x, s) => `${(x).toFixed(1).replace(/\.0$/, '')}${s}`;
    if (a >= 1e9) return c(v / 1e9, 'B');
    if (a >= 1e6) return c(v / 1e6, 'M');
    if (a >= 1e3) return c(v / 1e3, 'K');
    return String(v);
  };

  // Priority Songs states
  const [showAddPriorityModal, setShowAddPriorityModal] = useState(false);
  const [showExportPriorityModal, setShowExportPriorityModal] = useState(false);
  const [showExportFormatModal, setShowExportFormatModal] = useState(false);
  const [exportText, setExportText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedSong, setSelectedSong] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [expandedPriorityRow, setExpandedPriorityRow] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'last_complete_week_streams', order: 'desc' });
  const [editingTrackingLabel, setEditingTrackingLabel] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [timeRange, setTimeRange] = useState(30);
  const [selectedMetrics, setSelectedMetrics] = useState(['audio']);
  const [selectedRegion, setSelectedRegion] = useState('global');
  const [isMetricsOpen, setIsMetricsOpen] = useState(false);
  const [lagDays, setLagDays] = useState('2');
  const [showAdvancedAnalytics, setShowAdvancedAnalytics] = useState(false);

  // Add Album Modal States
  const [showAddAlbumModal, setShowAddAlbumModal] = useState(false);
  const [expandedAlbumRow, setExpandedAlbumRow] = useState(null);

  const queryClient = useQueryClient();
  
  // Priority Songs specific tracking label options
  const priorityTrackingLabelOptions = [
    { value: 'None', label: 'None' },
    { value: 'Priority Song', label: 'Priority Song' }
  ];
  
  // Metric options
  const metricOptions = [
    { id: 'total', label: 'Total Streams' },
    { id: 'audio', label: 'Audio Streams' },
    { id: 'video', label: 'Video Streams' },
    { id: 'ad', label: 'Ad-Supported' },
    { id: 'premium', label: 'Premium' },
    { id: 'ondemand', label: 'On-Demand' },
    { id: 'programmed', label: 'Programmed' }
  ];

  useEffect(() => {
    const fetchTotals = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/streaming/totals`);
        setTotals(response.data);
      } catch (error) {
        console.error('Error fetching totals:', error);
      }
    };

    const fetchMaxDates = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/streaming/max-dates`, {
          params: { is_pgp: false }
        });
        setMaxDates(response.data);
      } catch (error) {
        console.error('Error fetching max dates:', error);
      }
    };

    fetchTotals();
    fetchMaxDates();
  }, []);

  const handleRosterExport = async (format) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/roster/export`, {
        responseType: 'blob',
        params: { format, audio_only: audioOnly }
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `roster_streaming_report_${new Date().toISOString().split('T')[0]}.${format === 'csv' ? 'csv' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      setShowExportModal(false);
    } catch (error) {
      console.error('Error exporting roster report:', error);
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/streaming/combined-export`, {
        responseType: 'blob',
        params: {
          format: format,
          audio_only: audioOnly
        }
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const fileExtension = format === 'csv' ? 'csv' : 'pdf';
      link.setAttribute('download', `streaming_combined_${new Date().toISOString().split('T')[0]}.${fileExtension}`);
      
      // Append to html link element page
      document.body.appendChild(link);
      
      // Start download
      link.click();
      
      // Clean up and remove the link
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      setShowExportModal(false);
    } catch (error) {
      console.error('Error exporting:', error);
    }
  };
  
  // Priority Songs functions
  const handleSort = (key, explicitOrder = null) => {
    setSortConfig(prev => ({
      key,
      order: explicitOrder || (prev.key === key && prev.order === 'desc' ? 'asc' : 'desc')
    }));
  };
  
  const handlePriorityRowClick = (item) => {
    setExpandedPriorityRow(expandedPriorityRow === item.id ? null : item.id);
  };
  
  const toggleMetric = (metricId) => {
    setSelectedMetrics(prev => {
      if (prev.includes(metricId)) {
        return prev.filter(id => id !== metricId);
      }
      return [...prev, metricId];
    });
  };
  
  // Get the label of the first selected metric for display
  const selectedMetricLabel = metricOptions.find(opt => selectedMetrics.includes(opt.id))?.label || 'Select Type';
  
  // Mutation for updating tracking label
  const updateTrackingLabelMutation = useMutation(
    async ({ songId, trackingLabel }) => {
      const response = await axios.put(`${API_BASE_URL}/update-song-tracking-label/${songId}`, {
        tracking_label: trackingLabel
      });
      return response.data;
    },
    {
      onSuccess: () => {
        // Refetch the data to update the table
        queryClient.invalidateQueries(['priority-songs']);
      },
      onError: (error) => {
        console.error('Error updating tracking label:', error);
        alert('Error updating tracking label. Please try again.');
      }
    }
  );
  
  const handleTrackingLabelChange = (id, newLabel) => {
    updateTrackingLabelMutation.mutate({
      songId: id,
      trackingLabel: newLabel
    });
  };

  // Mutation for updating album tracking label
  const updateAlbumTrackingLabelMutation = useMutation(
    async ({ albumId, trackingLabel }) => {
      const response = await axios.put(`${API_BASE_URL}/update-album-tracking-label/${albumId}`, {
        tracking_label: trackingLabel
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['encore-albums']);
      },
      onError: (error) => {
        console.error('Error updating album tracking label:', error);
        alert('Error updating tracking label. Please try again.');
      }
    }
  );

  const handleAlbumTrackingLabelChange = (id, newLabel) => {
    updateAlbumTrackingLabelMutation.mutate({
      albumId: id,
      trackingLabel: newLabel
    });
  };

  const handleAlbumRowClick = (item) => {
    setExpandedAlbumRow(expandedAlbumRow === item.id ? null : item.id);
  };

  // Tracking label options for albums
  const albumTrackingLabelOptions = [
    { value: 'None', label: 'None' },
    { value: 'High Priority', label: 'High Priority' },
    { value: 'Medium Priority', label: 'Medium Priority' },
    { value: 'Low Priority', label: 'Low Priority' },
    { value: 'Tracking', label: 'Tracking' },
    { value: 'Archive', label: 'Archive' }
  ];

  // Album columns definition (matching Unsigned page structure)
  const albumColumns = [
    { key: 'artist', label: 'Artist', sortable: true },
    { key: 'title', label: 'Album', sortable: true },
    { key: 'tracking_label', label: 'Priority', sortable: true },
    { key: 'global_building_week_streams', label: 'Global Building', sortable: true, align: 'right' },
    { key: 'global_building_week_change_percent', label: 'Global Building %', sortable: true, align: 'right', isPercent: true },
    { key: 'global_last_complete_week_streams', label: 'Global Last Week', sortable: true, align: 'right' },
    { key: 'global_week_over_week_change_percent', label: 'Global % Change', sortable: true, align: 'right', isPercent: true },
    { key: 'us_streams_percent', label: 'US %', sortable: true, align: 'right', isPercent: true },
    ...(showAdvancedAnalytics ? [
      { key: 'weeks_grown_over_0_pct', label: 'Weeks Grown >0%', sortable: true, align: 'right' },
      { key: 'weeks_grown_over_neg5_pct', label: 'Weeks Grown >-5%', sortable: true, align: 'right' },
      { key: 'is_peak_daily_30days', label: 'Peak Daily 30d', sortable: true, align: 'center', isBoolean: true },
      { key: 'is_peak_daily_180days', label: 'Peak Daily 180d', sortable: true, align: 'center', isBoolean: true },
      { key: 'is_peak_weekly_30days', label: 'Peak Weekly 30d', sortable: true, align: 'center', isBoolean: true },
      { key: 'is_peak_weekly_180days', label:'Peak Weekly 180d', sortable: true, align: 'center', isBoolean: true }
    ] : [])
  ];

  // Add priority song mutation
  const addPrioritySongMutation = useMutation(
    async (songId) => {
      const response = await axios.post(`${API_BASE_URL}/add-priority-song`, {
        luminate_id: songId
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['priority-songs']);
        resetAddModal();
        alert(`Successfully added ${selectedSong.title} as Priority Song!`);
      },
      onError: (error) => {
        console.error('Error adding priority song:', error);
        setIsAdding(false);
        const errorMessage = error.response?.data?.error || 'Error adding priority song. Please try again.';
        alert(errorMessage);
      }
    }
  );
  
  const resetAddModal = () => {
    setShowAddPriorityModal(false);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedSong(null);
    setIsSearching(false);
    setIsAdding(false);
  };

  
  const handleSearchSongs = async () => {
    if (!searchQuery.trim()) return;
    setIsSearching(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/search-songs-by-group`, {
        query: searchQuery,
        group: 'Encore'
      });
      setSearchResults(response.data.results || []);
    } catch (error) {
      console.error('Error searching songs:', error);
    } finally {
      setIsSearching(false);
    }
  };
  
  const handleAddPrioritySong = async () => {
    if (!selectedSong) return;
    setIsAdding(true);
    addPrioritySongMutation.mutate(selectedSong.id);
  };
  
  const handleExportPrioritySongs = async (format = 'text') => {
    try {
      // Fetch all priority songs for export with current sorting
      const response = await axios.get(`${API_BASE_URL}/song-summaries`, {
        params: {
          group: 'Priority Song',
          per_page: 1000, // Get all priority songs
          lag_days: lagDays,
          sort_by: sortConfig.key,
          sort_order: sortConfig.order
        }
      });
      
      const songs = response.data.data;
      
      if (!songs || songs.length === 0) {
        alert('No priority songs to export');
        return;
      }
      
      // Format songs in email style with plain text
      let plainText = '';
      
      songs.forEach((song, index) => {
        if (index > 0) plainText += '\n\n';
        
        plainText += `${song.artist} – "${song.title}"\n`;
        plainText += `Global Streaming Activity\n`;
        plainText += `• WTD TP: ${formatNumber(song.global_building_week_streams || 0)}`;
        if (song.global_building_week_change_percent !== null && song.global_building_week_change_percent !== undefined) {
          const change = song.global_building_week_change_percent;
          const sign = change > 0 ? '+' : '';
          plainText += ` (${sign}${change.toFixed(1)}%)`;
        }
        plainText += `\n• TP: ${formatNumber(song.global_last_complete_week_streams || 0)}`;
        if (song.global_week_over_week_change_percent !== null && song.global_week_over_week_change_percent !== undefined) {
          const change = song.global_week_over_week_change_percent;
          const sign = change > 0 ? '+' : '';
          plainText += ` (${sign}${change.toFixed(1)}%)`;
        }
        plainText += `\n\nUS Streaming Activity\n`;
        plainText += `• WTD TP: ${formatNumber(song.us_building_week_streams || 0)}`;
        if (song.us_building_week_change_percent !== null && song.us_building_week_change_percent !== undefined) {
          const change = song.us_building_week_change_percent;
          const sign = change > 0 ? '+' : '';
          plainText += ` (${sign}${change.toFixed(1)}%)`;
        }
        plainText += `\n• TP: ${formatNumber(song.us_last_complete_week_streams || 0)}`;
        if (song.us_week_over_week_change_percent !== null && song.us_week_over_week_change_percent !== undefined) {
          const change = song.us_week_over_week_change_percent;
          const sign = change > 0 ? '+' : '';
          plainText += ` (${sign}${change.toFixed(1)}%)`;
        }
      });
      
      if (format === 'pdf') {
        // Export as PDF
        handleExportPDF();
      } else {
        // Show text preview
        setExportText(plainText);
        setShowExportPriorityModal(true);
      }
      
    } catch (error) {
      console.error('Error exporting priority songs:', error);
      alert('Error generating priority songs report');
    }
  };
  
  // const handleCopyToClipboard = async () => {
  //   try {
  //     await navigator.clipboard.writeText(exportText);
  //     alert('Priority songs report copied to clipboard!');
  //   } catch (error) {
  //     console.error('Error copying to clipboard:', error);
  //     alert('Error copying to clipboard');
  //   }
  // };
  
  const handleExportPDF = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/priority-songs-export`, {
        responseType: 'blob',
        params: {
          lag_days: lagDays,
          sort_by: sortConfig.key,
          sort_order: sortConfig.order
        }
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `priority_songs_${new Date().toISOString().split('T')[0]}.pdf`);
      
      // Append to html link element page
      document.body.appendChild(link);
      
      // Start download
      link.click();
      
      // Clean up and remove the link
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      alert('PDF exported successfully!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Error exporting PDF. Please try again.');
    }
  };
  
  const formatTextWithColors = (text) => {
    // Split by lines and format each line
    return text.split('\n').map((line, index) => {
      // Check if line is artist and song name (contains " – " pattern)
      if (line.includes(' – "') && line.includes('"')) {
        return (
          <div key={index} className="whitespace-pre underline font-medium">
            {line}
          </div>
        );
      }
      
      // Check if line contains percentage
      const percentMatch = line.match(/\(([+-]?\d+\.\d+)%\)/);
      if (percentMatch) {
        const percent = parseFloat(percentMatch[1]);
        const colorClass = percent > 0 ? 'text-green-600' : percent < 0 ? 'text-red-600' : 'text-gray-600';
        const boldClass = percent > 10 ? 'font-bold' : '';
        
        // Replace the percentage with colored version
        const beforePercent = line.substring(0, line.indexOf('('));
        const afterPercent = line.substring(line.indexOf(')') + 1);
        
        return (
          <div key={index} className="whitespace-pre">
            {beforePercent}
            <span className={`${colorClass} ${boldClass}`}>({percentMatch[1]}%)</span>
            {afterPercent}
          </div>
        );
      }
      
      return <div key={index} className="whitespace-pre">{line}</div>;
    });
  };
  
  const formatNumber = (num) => {
    if (!num || num === 0) return '0';
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(0) + 'K';
    }
    return num.toString();
  };
  
  // Define columns for the priority songs table
  const prioritySongColumns = [
    { key: 'artist', label: 'Artist', sortable: true },
    { key: 'title', label: 'Song', sortable: true },
    { key: 'tracking_label', label: 'Priority', sortable: true },
    { key: 'global_building_week_streams', label: 'Global Building', sortable: true, align: 'right' },
    { key: 'global_building_week_change_percent', label: 'Global Building %', sortable: true, align: 'right', isPercent: true },
    { key: 'global_last_complete_week_streams', label: 'Global Last Week', sortable: true, align: 'right' },
    { key: 'global_week_over_week_change_percent', label: 'Global % Change', sortable: true, align: 'right', isPercent: true },
    { key: 'us_building_week_streams', label: 'US Building', sortable: true, align: 'right' },
    { key: 'us_building_week_change_percent', label: 'US Building %', sortable: true, align: 'right', isPercent: true },
    { key: 'us_last_complete_week_streams', label: 'US Last Week', sortable: true, align: 'right' },
    { key: 'us_week_over_week_change_percent', label: 'US % Change', sortable: true, align: 'right', isPercent: true },
    { key: 'us_streams_percent', label: 'US %', sortable: true, align: 'right', isPercent: true }
  ];
  
  // Render expanded content for priority songs
  const renderExpandedContent = (metrics) => {
    return (
      <div className="space-y-4">
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <select
              value={timeRange}
              onChange={e => setTimeRange(Number(e.target.value))}
              className="w-1/2 px-4 py-2 border rounded-lg text-sm"
            >
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 180 days</option>
              <option value={365}>Last year</option>
              <option value={9999}>All Time</option>
            </select>

            <select
              value={selectedRegion}
              onChange={e => setSelectedRegion(e.target.value)}
              className="w-1/2 px-4 py-2 border rounded-lg text-sm"
            >
              <option value="global">Global</option>
              <option value="us">US</option>
            </select>
          </div>

          <div className="relative">
            <button
              onClick={() => setIsMetricsOpen(!isMetricsOpen)}
              className="w-full px-4 py-2 border rounded-lg text-sm text-blue-600 bg-white flex justify-between items-center"
            >
              {selectedMetricLabel}
              <ChevronDownIcon className="h-5 w-5 text-gray-400" />
            </button>
            
            {isMetricsOpen && (
              <div className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg">
                {metricOptions.map(option => (
                  <button
                    key={option.id}
                    onClick={() => toggleMetric(option.id)}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                      selectedMetrics.includes(option.id) ? 'text-blue-600 bg-blue-50' : 'text-gray-700'
                    }`}
                  >
                    {option.label}
                    {selectedMetrics.includes(option.id) && (
                      <CheckIcon className="h-5 w-5 text-blue-600" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="h-[250px] sm:h-[400px] w-full">
          {metrics && (
            <MetricsChart
              dates={metrics.dates}
              metrics={metrics.metrics}
              selectedMetrics={selectedMetrics}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 pt-1 pb-8 sm:py-4">
      <div className="flex flex-row justify-between items-start sm:items-center gap-3 mb-3 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold">Streaming Analytics</h1>
          {/* Dates: one compact line on mobile, separate lines on desktop. */}
          {(maxDates.weekly_date || maxDates.daily_date) && (
            <p className="text-[11px] sm:text-sm text-gray-500 sm:text-gray-600 mt-0.5 sm:mt-1">
              <span className="sm:hidden">
                {maxDates.weekly_date && `Wk ${maxDates.weekly_date}`}
                {maxDates.weekly_date && maxDates.daily_date && ' · '}
                {maxDates.daily_date && `Day ${maxDates.daily_date}`}
              </span>
              <span className="hidden sm:inline">
                {maxDates.weekly_date && <>Week Ending: {maxDates.weekly_date}<br /></>}
                {maxDates.daily_date && <>Daily Ending: {maxDates.daily_date}</>}
              </span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
        {rosterMode && (
          <button
            onClick={() => setShowAddRosterSong(true)}
            className="self-start sm:self-auto bg-green-500 hover:bg-green-600 text-white font-medium text-sm py-2 px-3 rounded-lg shadow-sm transition"
          >
            Add Song
          </button>
        )}
        {canManageRoster && (
          <button
            onClick={toggleRosterMode}
            className={`self-start sm:self-auto text-sm font-medium py-2 px-3 rounded-lg border transition ${
              rosterMode ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
            title="Show or hide roster management"
          >
            Roster management{rosterMode ? ': On' : ''}
          </button>
        )}
        {activeTab === 'priority' && !rosterMode ? (
          <button
            onClick={() => setShowExportFormatModal(true)}
            className="self-start sm:self-auto bg-emerald-500 hover:bg-emerald-600 text-white font-medium text-sm py-2 px-4 rounded-lg shadow-sm hover:shadow transition shrink-0"
          >
            Export Priority Songs
          </button>
        ) : (
          <button
            onClick={() => setShowExportModal(true)}
            className="self-start sm:self-auto bg-blue-500 hover:bg-blue-600 text-white font-medium text-sm py-2 px-4 rounded-lg shadow-sm hover:shadow transition shrink-0"
          >
            Export Full Report
          </button>
        )}
        </div>
      </div>

      {showAddRosterSong && <AddRosterSongModal onClose={() => setShowAddRosterSong(false)} />}

      {/* Export Format Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Choose Export Format</h3>
                <button
                  onClick={() => setShowExportModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="space-y-3">
                <button
                  onClick={() => handleExport('pdf')}
                  className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="h-5 w-5 mr-2 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                  </svg>
                  Export as PDF
                </button>
                <button
                  onClick={() => handleExport('csv')}
                  className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="h-5 w-5 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  Export as CSV
                </button>
                {rosterMode && (
                  <div className="pt-3 mt-1 border-t border-gray-200 space-y-3">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Roster report (new)</p>
                    <button
                      onClick={() => handleRosterExport('pdf')}
                      className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Roster Report as PDF
                    </button>
                    <button
                      onClick={() => handleRosterExport('csv')}
                      className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Roster Report as CSV
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {totals && (() => {
        const changeColor = (v) =>
          parseFloat(v) > 0 ? 'text-green-600' : parseFloat(v) < 0 ? 'text-red-600' : 'text-gray-500';
        // One card per region as a single row: left = total, right = audio
        // (top) / video (bottom) with their % changes.
        const Region = ({ title, total, totalChg, audio, audioChg, video, videoChg }) => (
          <div className="bg-white px-3.5 py-3 sm:px-5 sm:py-4 rounded-xl ring-1 ring-gray-200/70 shadow-sm flex items-center justify-between gap-4">
            {/* Left: region total */}
            <div className="min-w-0">
              <h3 className="text-[11px] sm:text-sm font-semibold text-gray-500 uppercase tracking-wide">{title}</h3>
              <div className="flex items-baseline gap-2">
                <span className="text-xl sm:text-3xl font-bold tabular-nums leading-tight">{fmtTotal(total)}</span>
                <span className={`text-xs sm:text-sm font-medium ${changeColor(totalChg)}`}>{totalChg}</span>
              </div>
            </div>
            {/* Right: audio over video — aligned columns (label · value · %). */}
            <div className="grid grid-cols-[auto_auto_auto] items-baseline gap-x-2 gap-y-1 shrink-0">
              <span className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wide">Audio</span>
              <span className="text-sm sm:text-base font-semibold tabular-nums text-right">{fmtTotal(audio)}</span>
              <span className={`text-[10px] sm:text-xs font-medium text-right ${changeColor(audioChg)}`}>{audioChg}</span>
              <span className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wide">Video</span>
              <span className="text-sm sm:text-base font-semibold tabular-nums text-right">{fmtTotal(video)}</span>
              <span className={`text-[10px] sm:text-xs font-medium text-right ${changeColor(videoChg)}`}>{videoChg}</span>
            </div>
          </div>
        );
        return (
          <div className="mb-4 sm:mb-8">
            <h2 className="text-xs sm:text-xl font-semibold text-gray-500 sm:text-gray-900 uppercase sm:normal-case tracking-wide sm:tracking-normal mb-1.5 sm:mb-4">Total Streams Last Week</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-4">
              <Region
                title="Global"
                total={totals.global_last_week} totalChg={totals.global_week_over_week_change}
                audio={totals.global_audio_last_week} audioChg={totals.global_audio_week_over_week_change}
                video={totals.global_video_last_week} videoChg={totals.global_video_week_over_week_change}
              />
              <Region
                title="United States"
                total={totals.us_last_week} totalChg={totals.us_week_over_week_change}
                audio={totals.us_audio_last_week} audioChg={totals.us_audio_week_over_week_change}
                video={totals.us_video_last_week} videoChg={totals.us_video_week_over_week_change}
              />
            </div>
          </div>
        );
      })()}
      
      {rosterMode ? <RosterManager /> : (<>
      <div className="sm:border-b sm:border-gray-200 mb-4 sm:mb-6">
        <div className="flex flex-row items-center justify-between gap-2 sm:gap-3">
          {/* Mobile: compact dropdown selector. Desktop: tab bar. */}
          <select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
            className="sm:hidden shrink-0 w-auto px-2.5 py-1.5 bg-white border border-gray-300 rounded-lg text-xs font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400"
          >
            {TABS.map(tab => (
              <option key={tab.id} value={tab.id}>{tab.label}</option>
            ))}
          </select>
          <nav className="hidden sm:flex -mb-px gap-8">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                  ${activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                `}
              >
                {tab.label}
              </button>
            ))}
          </nav>

          {(activeTab === 'priority' || activeTab === 'albums') ? (
            <div className="flex items-center justify-end gap-2 sm:gap-4 sm:pb-4">
              {/* Lag Toggle */}
              <div className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-600">
                <span><span className="sm:hidden">Lag:</span><span className="hidden sm:inline">Building Week Lag:</span></span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setLagDays('2')}
                    className={`px-2.5 sm:px-3 py-1 rounded-lg text-xs font-medium transition ${
                      lagDays === '2'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    <span className="sm:hidden">2d</span><span className="hidden sm:inline">2 Days</span>
                  </button>
                  <button
                    onClick={() => setLagDays('3')}
                    className={`px-2.5 sm:px-3 py-1 rounded-lg text-xs font-medium transition ${
                      lagDays === '3'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    <span className="sm:hidden">3d</span><span className="hidden sm:inline">3 Days</span>
                  </button>
                </div>
              </div>

              {/* Advanced Analytics Toggle */}
              <label className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-600 whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={showAdvancedAnalytics}
                  onChange={(e) => setShowAdvancedAnalytics(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="sm:hidden">Advanced</span>
                <span className="hidden sm:inline">Advanced Analytics</span>
              </label>
            </div>
          ) : (
            <label className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-600 whitespace-nowrap sm:pb-4">
              <input
                type="checkbox"
                checked={audioOnly}
                onChange={(e) => setAudioOnly(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="sm:hidden">Audio Only</span>
              <span className="hidden sm:inline">Audio Streams Only</span>
            </label>
          )}
          
        </div>
      </div>

      {/* Full-bleed on mobile: cancel the page (px-4) + main (px-1) padding so
          the table stretches edge-to-edge; normal flow at sm+. */}
      <div className="-mx-5 sm:mx-0">
        {activeTab === 'priority' ? (
          <StreamingTable
            type="song"
            queryKey={['priority-songs', 'song']}
            apiEndpoint={`${API_BASE_URL}/song-summaries`}
            columns={prioritySongColumns}
            sortConfig={sortConfig}
            onSort={handleSort}
            onRowClick={handlePriorityRowClick}
            expandedRow={expandedPriorityRow}
            onTrackingLabelChange={handleTrackingLabelChange}
            editingTrackingLabel={editingTrackingLabel}
            setEditingTrackingLabel={setEditingTrackingLabel}
            trackingLabelOptions={priorityTrackingLabelOptions}
            renderExpandedContent={() => renderExpandedContent()}
            selectedRegion={selectedRegion}
            showArchived={showArchived}
            setShowArchived={setShowArchived}
            onAddClick={() => setShowAddPriorityModal(true)}
            group="Priority Song"
            lagDays={lagDays}
            enableSelection={false}
          />
        ) : activeTab === 'albums' ? (
          <StreamingTable
            type="album"
            queryKey={['encore-albums', 'album']}
            apiEndpoint={`${API_BASE_URL}/album-summaries`}
            columns={albumColumns}
            sortConfig={sortConfig}
            onSort={handleSort}
            onRowClick={handleAlbumRowClick}
            expandedRow={expandedAlbumRow}
            onTrackingLabelChange={handleAlbumTrackingLabelChange}
            editingTrackingLabel={editingTrackingLabel}
            setEditingTrackingLabel={setEditingTrackingLabel}
            trackingLabelOptions={albumTrackingLabelOptions}
            renderExpandedContent={() => renderExpandedContent()}
            selectedRegion={selectedRegion}
            showArchived={showArchived}
            setShowArchived={setShowArchived}
            onAddClick={() => setShowAddAlbumModal(true)}
            group="Encore"
            lagDays={lagDays}
            enableSelection={false}
          />
        ) : (
          <StreamingSummaryTable
            type={TABS.find(tab => tab.id === activeTab).type}
            audioOnly={audioOnly}
            group={activeTab === 'songs' ? 'Encore' : null}
            onAddClick={null}
          />
        )}
      </div>
      </>)}
      
      {/* Add Priority Song Modal */}
      {showAddPriorityModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Add Priority Song from Encore Catalog
                </h3>
                <button
                  onClick={resetAddModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search Encore Songs
                  </label>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearchSongs()}
                    placeholder="Enter song title to search..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isSearching}
                  />
                </div>
                
                <button
                  onClick={handleSearchSongs}
                  disabled={isSearching || !searchQuery.trim()}
                  className="w-full flex items-center justify-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isSearching ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Searching...
                    </>
                  ) : (
                    'Search'
                  )}
                </button>
                
                {searchResults.length > 0 && (
                  <>
                    <div className="max-h-60 overflow-y-auto">
                      {searchResults.map((song) => (
                        <div
                          key={song.id}
                          onClick={() => setSelectedSong(song)}
                          className={`p-3 border rounded-md mb-2 cursor-pointer hover:bg-gray-50 ${
                            selectedSong?.id === song.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                          }`}
                        >
                          <div className="font-medium">{song.title}</div>
                          <div className="text-sm text-gray-500">{song.artist}</div>
                          {song.isrc && (
                            <div className="text-xs text-gray-400">
                              ISRC: {song.isrc}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    <button
                      onClick={handleAddPrioritySong}
                      disabled={isAdding || !selectedSong}
                      className="w-full flex items-center justify-center px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {isAdding ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Adding...
                        </>
                      ) : (
                        <>
                          <svg className="h-5 w-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add as Priority Song
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Export Priority Songs Modal */}
      {showExportPriorityModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Priority Songs Export Preview
                </h3>
                <button
                  onClick={() => setShowExportPriorityModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="p-4 rounded-lg max-h-96 overflow-y-auto border">
                  <div className="text-sm">
                    {formatTextWithColors(exportText)}
                  </div>
                </div>
                
                {/* <div className="flex gap-2">
                  <button
                    onClick={handleCopyToClipboard}
                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded"
                  >
                    Copy Text to Clipboard
                  </button>
                  <button
                    onClick={handleExportPDF}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded"
                  >
                    Export as PDF
                  </button>
                  <button
                    onClick={() => setShowExportPriorityModal(false)}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium py-2 px-4 rounded"
                  >
                    Close
                  </button>
                </div>
                
                <p className="text-sm text-gray-600">
                  Preview shows colored percentages. Copy text for emails or export as PDF for formatted documents.
                </p> */}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Export Format Selection Modal */}
      {showExportFormatModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Choose Export Format</h3>
                <button
                  onClick={() => setShowExportFormatModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setShowExportFormatModal(false);
                    handleExportPrioritySongs('pdf');
                  }}
                  className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="h-5 w-5 mr-2 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                  </svg>
                  Export as PDF
                </button>
                <button
                  onClick={() => {
                    setShowExportFormatModal(false);
                    handleExportPrioritySongs('text');
                  }}
                  className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="h-5 w-5 mr-2 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/>
                    <path fillRule="evenodd" d="M4 5a2 2 0 012-2v1a1 1 0 001 1h6a1 1 0 001-1V3a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/>
                  </svg>
                  Export as Text
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Album Modal */}
      <AddAlbumModal
        isOpen={showAddAlbumModal}
        onClose={() => setShowAddAlbumModal(false)}
        group="Encore"
        queryKeyToInvalidate={['summaries', 'album']}
      />
    </div>
  );
} 